import { useCallback, useEffect, useRef, useState } from 'react';
import ReviewCard from './ReviewCard';
import { useCardGestures } from './useCardGestures';
import { ReviewCard as ReviewCardModel } from '../../../sdk/@types/Revisao';
import './ReviewCard.css';

function transitionDurationSeconds(raw: string): number {
  const first = raw.split(',')[0]?.trim() ?? '';
  const value = Number.parseFloat(first);
  if (!Number.isFinite(value)) return 0;
  return first.endsWith('ms') ? value / 1000 : value;
}

export interface ReviewCardFlipProps {
  card: ReviewCardModel;
  /** Controlled flip state (owned by the study view). */
  flipped: boolean;
  /** Called on a tap that isn't a swipe or a mask reveal. */
  onToggleFlip: () => void;
  /** Called after a swipe fling completes. 1 = next, -1 = previous. */
  onSwipe?: (dir: 1 | -1) => void;
  resolveImageUrl?: (fileKey: string) => string;
  /** Matéria name shown as a badge on flashcard faces. */
  subjectName?: string;
  className?: string;
  /**
   * Header slot for the currently-visible face only. Both faces stay
   * mounted at once for the 3D flip, so this is forwarded to whichever one
   * matches `flipped` and withheld from the other — otherwise the slot's
   * content would mount twice at once. Omit it and no edit entry is rendered.
   */
  editSlot?: React.ReactNode;
  /**
   * Footer slot for the face the flip has settled on. Withheld from both
   * faces while the turn is in progress. Omit it and no rating footer is rendered.
   */
  ratingSlot?: React.ReactNode;
  /** Opens the verso for the asked marking. Passed to both faces; does not record a rating. */
  onRevelar?: () => void;
}

/**
 * Animation wrapper around the presentational <ReviewCard/>.
 *
 * - 3D flip: two faces (front = card, back = flipped card) grid-stacked and
 *   rotated on Y. The parent stays the source of truth for `flipped`.
 * - Swipe: live tilt while dragging, fling-out past the threshold, then the
 *   parent advances via `onSwipe`.
 * Rating buttons stay owned by the study view (keyboard shortcuts, submitting
 * state) — it renders <RatingBar/> and hands the result through `ratingSlot`,
 * mounted only after the flip settles. `editSlot` still follows the visible face.
 */
export default function ReviewCardFlip({
  card,
  flipped,
  onToggleFlip,
  onSwipe,
  resolveImageUrl,
  subjectName,
  className = '',
  editSlot,
  ratingSlot,
  onRevelar,
}: ReviewCardFlipProps) {
  const [out, setOut] = useState<'left' | 'right' | null>(null);
  const busy = useRef(false);
  const innerRef = useRef<HTMLDivElement>(null);
  const [settledFlip, setSettledFlip] = useState(flipped);

  useEffect(() => {
    if (settledFlip === flipped) return;

    const inner = innerRef.current;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = inner ? transitionDurationSeconds(getComputedStyle(inner).transitionDuration) : 0;

    if (!inner || reducedMotion || duration < 0.05) {
      setSettledFlip(flipped);
      return;
    }

    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      setSettledFlip(flipped);
    };

    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== inner || event.propertyName !== 'transform') return;
      settle();
    };

    inner.addEventListener('transitionend', onTransitionEnd);
    const timeoutId = window.setTimeout(settle, duration * 1000 + 50);

    return () => {
      inner.removeEventListener('transitionend', onTransitionEnd);
      window.clearTimeout(timeoutId);
    };
  }, [flipped, settledFlip]);

  // The footer is on the rotating face; a mid-turn paint shows it in 3D.
  const ratingForSettledFace = settledFlip === flipped ? ratingSlot : undefined;

  const fling = useCallback(
    (dir: 1 | -1) => {
      if (busy.current) return;
      busy.current = true;
      setOut(dir > 0 ? 'left' : 'right');
      window.setTimeout(() => {
        onSwipe?.(dir);
        setOut(null);
        busy.current = false;
      }, 280); // keep in sync with .rc-swipe transition duration
    },
    [onSwipe],
  );

  const { dragX, dragging, handlers } = useCardGestures({
    onSwipe: fling,
    onFlip: onToggleFlip,
    disabled: out !== null,
  });

  const style: React.CSSProperties =
    !out && dragging
      ? { transform: `translateX(${dragX}px) rotate(${dragX * 0.02}deg)` }
      : {};

  return (
    <div
      className={`rc-swipe ${dragging ? 'is-dragging' : ''} ${out ? `rc-out-${out}` : ''} ${className}`}
      style={style}
      {...handlers}
    >
      <div className="rc-flip">
        <div ref={innerRef} className={`rc-flip-inner ${flipped ? 'is-flipped' : ''}`}>
          <div className="rc-face rc-face--front" aria-hidden={flipped}>
            <ReviewCard
              card={card}
              flipped={false}
              resolveImageUrl={resolveImageUrl}
              subjectName={subjectName}
              editSlot={flipped ? undefined : editSlot}
              ratingSlot={flipped ? undefined : ratingForSettledFace}
              onRevelar={onRevelar}
            />
          </div>
          <div className="rc-face rc-face--back" aria-hidden={!flipped}>
            <ReviewCard
              card={card}
              flipped
              resolveImageUrl={resolveImageUrl}
              subjectName={subjectName}
              editSlot={flipped ? editSlot : undefined}
              ratingSlot={flipped ? ratingForSettledFace : undefined}
              onRevelar={onRevelar}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
